<template>
	<div ref="root" class="base-dropdown" :class="additionalClasses">
		<div ref="backdrop" class="base-dropdown__backdrop"></div>

		<div class="base-dropdown__control">
			<slot v-bind="{ toggleOpen }" />
		</div>

		<div ref="dropdown" class="base-dropdown__dropdown">
			<slot name="dropdown" />
		</div>
	</div>
</template>

<script lang="ts">
import { defineComponent } from 'vue';

export default defineComponent({
	props: {
		align: {
			type: String as () => 'left' | 'right' | 'center',
			default: 'center',
		},
	},

	emits: ['toggle-menu'],

	data() {
		return {
			isOpen: false,
		};
	},

	computed: {
		additionalClasses(): Array<string> {
			const classes = new Array<string>();

			classes.push(`base-dropdown--align-${this.align}`);

			if (this.isOpen) {
				classes.push(`base-dropdown--is-open`);
			}

			return classes;
		},
	},

	watch: {
		isOpen: {
			handler() {
				this.$nextTick(() => {
					this.scaleBackdrop();
				});
			},
			immediate: true,
		},
	},

	mounted() {
		this.onClickOut = this.onClickOut.bind(this);
		document.documentElement.addEventListener('click', this.onClickOut);
		window.addEventListener('resize', this.onResize);
	},

	beforeUnmount() {
		document.documentElement.removeEventListener('click', this.onClickOut);
		window.removeEventListener('resize', this.onResize);
	},

	methods: {
		toggleOpen(): void {
			this.isOpen = !this.isOpen;
			this.$emit('toggle-menu', this.isOpen);
		},

		onClickOut({ target }: MouseEvent): void {
			if (
				this.isOpen &&
				target instanceof HTMLElement &&
				!(this.$el as HTMLElement).contains(target) &&
				this.$el !== target
			) {
				this.isOpen = false;
				this.$emit('toggle-menu', this.isOpen);
			}
		},

		scaleBackdrop() {
			if (this.isOpen && this.$refs.dropdown && this.$refs.backdrop) {
				const dropdown = this.$refs.dropdown as HTMLElement;
				const backdrop = this.$refs.backdrop as HTMLElement;
				const selfBbox = this.$el.getBoundingClientRect();
				const dropdownBbox = dropdown.getBoundingClientRect();

				backdrop.style.width = `${dropdownBbox.width}px`;
				backdrop.style.height = `${dropdownBbox.height + dropdownBbox.top - selfBbox.top}px`;
			}
		},

		onResize() {
			setTimeout(() => this.scaleBackdrop(), 100);
		},
	},
});
</script>

<style lang="scss">
.base-dropdown {
	$self: &;
	$width: 19rem;

	min-width: $width;
	position: relative;

	&__control {
		display: flex;
		justify-content: flex-end;
		min-width: $width;
	}

	&__backdrop {
		$backdrop-offset: 0.4rem;

		display: none;
		position: absolute;
		top: 0;

		&::before {
			content: '';
			display: block;
			position: absolute;
			top: 0;
			right: -1 * $backdrop-offset;
			bottom: -1 * $backdrop-offset;
			left: -1 * $backdrop-offset;
			border-radius: 1.2rem;
			background-color: $color-white;
			box-shadow: 0 0 1rem rgba(0, 0, 0, 0.1);
		}
	}

	&__dropdown {
		display: none;
		min-width: $width;
		padding: 1.2rem 1.5rem;
		position: absolute;
		top: 100%;
		box-sizing: border-box;
		border-radius: 0.8rem;
		background: #fff;
	}

	&--align-left {
		#{$self}__dropdown,
		#{$self}__backdrop {
			left: 0;
		}
	}

	&--align-right {
		#{$self}__dropdown,
		#{$self}__backdrop {
			right: 0;
		}
	}

	&--align-center {
		#{$self}__dropdown,
		#{$self}__backdrop {
			margin-left: math.div($width, -2);
			left: 50%;
		}
	}

	&--is-open {
		#{$self}__dropdown {
			display: block;
			transform-origin: 50% 0;
			animation: animation-name-auto('in') 200ms 50ms forwards $easeOutQuad;
			// Chrome fix: Always keep content fully visible
			// Animation only controls transform, not opacity
			opacity: 1;
			box-shadow: 0 0 1rem rgba(0, 0, 0, 0.1);
			will-change: transform;

			// Ensure all child content has full opacity regardless of parent animations
			> * {
				opacity: 1 !important;
			}

			@keyframes #{animation-name-auto('in')} {
				0% {
					transform: translateY(-0.5rem);
					animation-timing-function: $linear;
				}

				30% {
					transform: translateY(-0.4rem);
				}

				to {
					transform: none;
				}
			}
		}

		#{$self}__backdrop {
			display: block;
			transform-origin: 100% 0;
			animation: animation-name-auto('in') 250ms $easeOutQuad;

			@keyframes #{animation-name-auto('in')} {
				0% {
					transform: translate(-0.5rem, 0.5rem) scale(0.85, 0.9);
					animation-timing-function: $linear;
				}

				30% {
					transform: scale(0.95, 0.95);
					animation-timing-function: $easeOutQuad;
				}

				to {
					transform: none;
				}
			}
		}

		#{$self}__control {
			button {
				border: 1px solid transparent;
			}
		}
	}
}
</style>
